import { deleteActivity, getActivitiesInRange } from '@/api/activity.api'
import { EMPTY_ACTIVITY, type Activity } from '@/common/activity.common'
import {
  activityToEvent,
  getInvoicedActivityConflictMessage,
  isInvoicedActivityConflict,
} from '@/helper/activity.helper'
import { isSameDay } from '@/utils/date.utils'
import { Tooltip } from '@/components/ui/tooltip'
import { Button, Card, Flex, Text, Box, Heading, HStack, Status, VStack } from '@chakra-ui/react'
import type {
  CustomContentGenerator,
  DateSelectArg,
  DayCellContentArg,
  EventContentArg,
  EventSourceInput,
  EventClickArg,
} from '@fullcalendar/core/index.js'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import React from 'react'
import { LuCalendarPlus, LuPencil, LuTrash2 } from 'react-icons/lu'
import '../../styles/components/activity/ActivityCalendar.css'
import { toaster } from '../ui/toaster/toasterInstance'
import DeleteActivityModal from './DeleteActivityModal'
import type { CreateActivityDTO } from '@/api/dto/activity.dto'
import { getBankById, getBanks, getServices } from '@/api/settings.api'
import { getOrders } from '@/api/orders.api'
import { useUserStore } from '@/stores/user.store'
import { useNavigate } from 'react-router-dom'
import { formatBankLabel, unitsToHours } from '@/helper/order.helper'
import { PeriodPager } from '@/components/common/PeriodPager'
// import { useLocation } from 'react-router-dom'
// import { useOnboardingStore } from '@/stores/onboarding.store'

/**
 * @component ActivityCalendar
 * @description
 * A full-featured calendar widget for displaying and managing activities using FullCalendar.
 *
 * It allows the user to:
 * - View activities in a **monthly calendar grid** with custom day cells showing weekday labels.
 * - Navigate between months using **previous/next month buttons**.
 * - **Select a date** to view activities for that specific day in a sidebar.
 * - **Create new activities** via a button that opens the CreateActivityPopup dialog.
 * - **Edit existing activities** by clicking the edit icon in the sidebar activity list.
 * - **Delete activities** with confirmation modal via the delete icon.
 * - Automatically **fetch activities** for the current month from the API.
 *
 * Includes:
 * - Custom **day cell rendering** with day number and abbreviated weekday.
 * - Custom **event rendering** with colored dots and time/title display.
 * - **Selectable dates** (single day only) with visual feedback.
 * - **Imperative handle** exposing a `rerender()` method for parent components to force resize.
 * - **Automatic data refresh** when the displayed month changes.
 * - **Error handling** with toast notifications for API failures.
 * - **Sidebar** showing activities for the selected date with edit/delete actions.
 * - Integration with CreateActivityPopup for create/edit workflows.
 * - Integration with DeleteActivityModal for safe deletion with confirmation.
 *
 * @prop {React.Ref<ActivityCalendarHandle>} ref - Forward ref exposing `rerender()` method.
 *
 * @interface ActivityCalendarHandle
 * @property {() => void} rerender - Forces the calendar to resize (useful after layout changes).
 *
 * @example
 * ```tsx
 * const calendarRef = useRef<ActivityCalendarHandle>(null);
 *
 * // Force calendar to resize after sidebar toggle
 * const handleSidebarToggle = () => {
 *   setSidebarOpen(!sidebarOpen);
 *   setTimeout(() => calendarRef.current?.rerender(), 300);
 * };
 *
 * <ActivityCalendar ref={calendarRef} />
 * ```
 */

dayjs.locale('de')

const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']

export interface ActivityCalendarHandle {
  rerender: () => void
  openCreateActivity: () => void
}

interface ActivityCalendarProps {
  hasCapacitySettingsSetup?: boolean
  hasUnionSettingsSetup?: boolean
}

const UNION_ID = 1

const UNION_DE_LABELS: Record<string, string> = {
  TOJ: 'ToJ',
  CONSULTING: 'Beratung',
  TRAINING: 'Schulung',
  TOJ_CUSTOMER: 'ToJ mit Kunde',
}

const ACTIVITY_LEGEND = [
  { label: 'Schulung', colorPalette: 'cyan' },
  { label: 'Beratung', colorPalette: 'purple' },
  { label: 'TOJ', colorPalette: 'red' },
  { label: 'TOJ mit Kunde', colorPalette: 'yellow' },
  { label: 'Andere', colorPalette: 'gray' },
]

function activityStatusColorPalette(serviceCode?: unknown) {
  switch (String(serviceCode ?? '').toUpperCase()) {
    case 'TRAINING':
      return 'cyan'
    case 'CONSULTING':
      return 'purple'
    case 'TOJ':
      return 'red'
    case 'TOJ_CUSTOMER':
      return 'yellow'
    case 'PILOT':
      return 'green'
    default:
      return 'gray'
  }
}

const ActivityCalendar = forwardRef<ActivityCalendarHandle, ActivityCalendarProps>(
  (props, ref) => {
    const { hasCapacitySettingsSetup, hasUnionSettingsSetup } = props
    const calendarRef = useRef<FullCalendar>(null)
    // This is the date for a selected day
    const [selectedDate, setSelectedDate] = useState<Date>(new Date())
    // const onboarding = useOnboardingStore()
    const [activityToEdit, setActivityToEdit] = useState<CreateActivityDTO | null>(null)
    // We set this special editId state to distinguish between creating a new activity and editing an existing one.
    const [editId, setEditId] = useState<string>()
    // This is the start of the currently displayed month
    const [calendarDate, setCalendarDate] = useState<Date>(
      dayjs(new Date()).startOf('month').toDate()
    )
    const [displayActivities, setDisplayActivities] = useState<Activity[]>([])
    const [activityToDelete, setActivityToDelete] = useState<string | null>(null)
    const [deleting, setDeleting] = useState<boolean>(false)
    const [banks, setBanks] = useState<{ label: string; value: number }[]>([])
    const [customers, setCustomers] = useState<{ label: string; value: number }[]>([])
    const user = useUserStore(s => s.user)
    const [acceptedOrders, setAcceptedOrders] = useState<any[]>([])
    const [serviceOptions, setServiceOptions] = useState<{ label: string; value: string }[]>([])

    const serviceLabelByCode = useMemo(() => {
      const map = new Map<string, string>()
      serviceOptions.forEach(s => map.set(s.value, s.label))
      return map
    }, [serviceOptions])

    // We need this handle to manually force the calendar to rerender after the sidebar
    // is toggled and therefore changes the available width.

    useEffect(() => {
      async function load() {
        try {
          const services = await getServices()
          setServiceOptions(
            (services ?? [])
              .filter((s: any) => s.active !== false)
              .sort(
                (a: any, b: any) =>
                  (a.displayOrder ?? 9999) - (b.displayOrder ?? 9999) ||
                  String(a.name).localeCompare(String(b.name))
              )
              .map((s: any) => ({ label: s.name, value: s.code }))
          )

          const response = await getBanks()
          const list = response.items ?? []

          const mapped = list.map((b: any) => ({
            label: formatBankLabel(b.name, b.name2, b.name3),
            value: b.id,
          }))

          setBanks(mapped)

          // find union bank from the top-level list
          const union = list.find((b: any) => (b.name ?? '').toLowerCase().includes('union'))

          if (union) {
            // fetch real data including associatedEntities
            const unionDetails = await getBankById(union.id)

            const assoc =
              unionDetails?.associatedEntities?.map((e: any) => ({
                label: formatBankLabel(e.name, e.name2, e.name3),
                value: e.id,
              })) ?? []

            setCustomers(assoc)
          } else {
            setCustomers([])
          }
        } catch (err) {
          console.error('Failed to load banks/customers', err)
        }
      }

      load()
    }, [])

    //Implement SIB-308
    useEffect(() => {
      // bail out if user not loaded (fixes 'user is possibly null')
      if (!user) return
      const userId = user.id
      if (!userId) return

      async function loadAcceptedOrders() {
        try {
          const currentYear = new Date().getFullYear()
          const res = await getOrders({ year: currentYear })

          // ✅ Correctly flatten: items -> year -> userId -> [orders]
          const items = (res as any).items ?? {}

          const allOrders: any[] = Object.values(items)
            .flatMap(
              yearObj => Object.values(yearObj as any) // arrays per userId
            )
            .flat()
            .filter(o => o && typeof o === 'object')

          const approvedOrders = allOrders.filter(
            o => o.status === 'APPROVED' && o.userId === userId
          )

          console.log('approvedOrders', approvedOrders)

          setAcceptedOrders(approvedOrders)
        } catch (err) {
          console.error('Failed to load accepted orders', err)
        }
      }

      loadAcceptedOrders()
    }, [user])

    // APPROVED orders give valid *bankIds* (these are the Leistungsempfänger)
    const allowedEntityIds = useMemo(() => {
      const set = new Set<number>()

      for (const o of acceptedOrders ?? []) {
        const bankId = Number((o as any).bankId)
        const customerId = Number((o as any).customerId)

        // new style: Union order -> entity is in customerId
        if (bankId === UNION_ID) {
          if (Number.isFinite(customerId) && customerId > 0) set.add(customerId)
          continue
        }

        // old style: entity was in bankId
        if (Number.isFinite(bankId) && bankId > 0) set.add(bankId)
      }

      return Array.from(set)
    }, [acceptedOrders])

    const filteredCustomers = useMemo(() => {
      return customers.filter(c => allowedEntityIds.includes(Number(c.value)))
    }, [customers, allowedEntityIds])

    const getActivities = useCallback(async () => {
      try {
        const endDate = dayjs(calendarDate).endOf('month').toDate()
        const res = await getActivitiesInRange(calendarDate, endDate)

        setDisplayActivities(res.items || [])
      } catch (e) {
        if (e instanceof Error) {
          toaster.error({
            title: 'Fehler beim Laden der Aktivitäten',
            description: e.message,
            type: 'error',
          })
        }
      }
    }, [calendarDate])

    useEffect(() => {
      getActivities()
    }, [getActivities])

    const nav = useNavigate()

    const events: EventSourceInput = useMemo(() => {
      return (displayActivities || []).map(activity => {
        const base = activityToEvent(activity)

        const rawCode =
          (activity as any).serviceCode || (activity as any).serviceType || (activity as any).type

        const code = String(rawCode ?? '')
          .trim()
          .toUpperCase()
          .replace(/[-\s]/g, '_')

        const customLabel =
          (activity as any).serviceName ||
          (activity as any).serviceLabel ||
          (activity as any).typeLabel ||
          null

        const isUnionActivity =
          Number((activity as any).bankId) === UNION_ID ||
          Number((activity as any).customerId) === UNION_ID

        const title = isUnionActivity
          ? (UNION_DE_LABELS[code] ?? code)
          : (customLabel ?? serviceLabelByCode.get(code) ?? String(rawCode ?? 'Aktivität'))

        const bankLabel =
          customers.find(c => Number(c.value) === Number((activity as any).customerId))?.label ??
          ''

        return {
          ...base,
          title,
          extendedProps: {
            ...(base as any).extendedProps,
            billingVariant: (activity as any).billingVariant ?? null,
            bankLabel,
            invoiced: Boolean((activity as any).invoiced),
            serviceCode:
              (activity as any).serviceCode ||
              (activity as any).serviceType ||
              (activity as any).type,
            units: Number(
              (activity as any).timeQuantity ??
                (activity as any).units ??
                (activity as any).quantity ??
                1
            ),
          },
        }
      })
    }, [displayActivities, serviceLabelByCode, customers])

    const getEventCountForDate = (date: Date) => {
      return (events || []).filter(event => {
        const eventDate = new Date(event.date as string)
        return (
          eventDate.getFullYear() === date.getFullYear() &&
          eventDate.getMonth() === date.getMonth() &&
          eventDate.getDate() === date.getDate()
        )
      }).length
    }
    const renderDayCell: CustomContentGenerator<DayCellContentArg> = info => {
      const isSelected = dayjs(info.date).isSame(selectedDate, 'day')
      const isToday = dayjs(info.date).isSame(new Date(), 'day')
      const isOtherMonth = !dayjs(info.date).isSame(calendarDate, 'month')
      const eventCount = getEventCountForDate(info.date)
      const extraCount = Math.max(0, eventCount - 4)
      return (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            padding: '10px 10px 8px 10px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            pointerEvents: 'none',
            background: isSelected ? 'var(--chakra-colors-bg-muted)' : 'transparent',
            color: isOtherMonth
              ? 'var(--chakra-colors-bg.muted)'
              : 'var(--chakra-colors-bg.inverted)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span
              style={{
                fontSize: '12px',
                fontWeight: isToday || isSelected ? 'medium' : 'normal',
                color:
                  isToday || isSelected ? 'blue.solid' : isOtherMonth ? 'bg.muted' : 'bg.inverted',
                lineHeight: 1,
              }}
            >
              {info.dayNumberText}
            </span>
          </div>

          <span
            style={{
              fontSize: '10px',
              fontWeight: 'normal',
              color: isOtherMonth ? 'bg.muted' : 'bg.inverted',
              lineHeight: 1,
            }}
          >
            {WEEKDAYS[info.date.getDay()]}
          </span>
          {extraCount > 0 && (
            <span
              style={{
                position: 'absolute',
                top: '32px',
                right: '48px',
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--chakra-colors-fg-muted)',
                lineHeight: 1,
              }}
            >
              +{extraCount}
            </span>
          )}
        </div>
      )
    }

    const renderEvent: CustomContentGenerator<EventContentArg> = info => {
      const color =
        info.event.extendedProps.color ||
        info.event.backgroundColor ||
        'var(--chakra-colors-fg-muted)'

      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            padding: 0,
            margin: 0,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 9999,
              background: color,
              display: 'inline-block',
            }}
          />
        </div>
      )
    }

    const editActivity = (id: string | number) => {
      const activity = (displayActivities || []).find(a => String(a.id) === String(id))

      if (!activity) {
        console.warn('Activity not found for id', id)
        return
      }

      // ✅ Convert Activity -> CreateActivityDTO (normalize location.type)
      const dto: CreateActivityDTO = {
        ...(activity as any),
        shortNoticeCancelled:
          typeof (activity as any).shortNoticeCancelled === 'boolean'
            ? (activity as any).shortNoticeCancelled
            : [
                'EIGHTY_PERCENT_TOJ',
                'EIGHTY_PERCENT_TRAINING',
                'EIGHTY_PERCENT_CONSULTING',
              ].includes(String((activity as any).billingVariant ?? '')),
        location: {
          ...(activity.location as any),
          type: String(activity.location?.type || '').toUpperCase() as 'ONSITE' | 'ONLINE',
        },
      }

      setActivityToEdit(dto)
      setEditId(String(id))
      nav(`/activities/${id}/edit`, {
        state: {
          values: dto,
          customers: banks,
          banks: filteredCustomers,
          tags: [],
          services: serviceOptions,
          acceptedOrders,
          editId: String(id),
        },
      })
    }
    const navDataReady = banks.length > 0 && serviceOptions.length > 0 && customers.length > 0 // your associatedEntities
    const openCreateActivityPage = useCallback(() => {
      // Prevent creating activities in the future
      // if (dayjs(selectedDate).isAfter(dayjs(), 'day')) {
      //   toaster.warning({
      //     title: 'Ungültiges Datum',
      //     description: 'Bitte wählen Sie ein Datum in der Vergangenheit oder heute.',
      //   })
      //   return
      // }

      if (!hasCapacitySettingsSetup) {
        toaster.warning({
          title: 'Kapazitätseinstellungen unvollständig',
          description:
            'Bitte richten Sie Ihre Kapazitätseinstellungen ein, bevor Sie Aktivitäten erstellen.',
          action: {
            label: 'Einrichten',
            onClick: () => nav('/settings/orders'),
          },
        })
        return
      }

      if (!hasUnionSettingsSetup) {
        toaster.warning({
          title: 'Leistungskonfiguration unvollständig',
          description:
            'Bitte richten Sie Ihre Leistungskonfiguration ein, bevor Sie Aktivitäten erstellen.',
          action: {
            label: 'Einrichten',
            onClick: () => nav('/settings/bank'),
          },
        })
        return
      }

      if (!navDataReady) {
        toaster.warning({
          title: 'Daten werden noch geladen',
          description: 'Bitte kurz warten…',
        })
        return
      }

      const initialValues = {
        ...EMPTY_ACTIVITY,
        date: selectedDate,
      }

      setActivityToEdit(initialValues)
      setEditId(undefined)
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      nav('/activities/new', {
        state: {
          values: initialValues,
          customers: banks, // Auftraggeber
          banks: filteredCustomers, // Leistungsempfänger
          tags: [],
          services: serviceOptions,
          acceptedOrders,
          editId: undefined,
        },
      })
    }, [
      selectedDate,
      hasCapacitySettingsSetup,
      hasUnionSettingsSetup,
      navDataReady,
      nav,
      banks,
      filteredCustomers,
      serviceOptions,
      acceptedOrders,
    ])

    useImperativeHandle(
      ref,
      () => ({
        rerender: () => {
          calendarRef.current?.doResize()
        },
        openCreateActivity: () => {
          openCreateActivityPage()
        },
      }),
      [openCreateActivityPage]
    )

    const onEventClick = (info: EventClickArg) => {
      if (info.event.id) {
        editActivity(info.event.id)
        if (info.event.start) {
          setSelectedDate(info.event.start)
        }
      }
    }

    // const closeEdit = () => {
    //   setActivityToEdit(null)
    //   setEditId(undefined)
    //   getActivities()
    // }

    const onDaySelect = (arg: DateSelectArg) => {
      // if (dayjs(arg.start).isAfter(dayjs(), 'day')) {
      //   toaster.warning({
      //     title: 'Ungültiges Datum',
      //     description: 'Bitte wählen Sie ein Datum in der Vergangenheit oder heute.',
      //   })
      //   return
      // }

      setSelectedDate(arg.start)
    }

    const getEventsForSelection = () => {
      return events.filter(event => {
        const eventDate = new Date(event.date as string)
        return (
          eventDate.getFullYear() === selectedDate.getFullYear() &&
          eventDate.getMonth() === selectedDate.getMonth() &&
          eventDate.getDate() === selectedDate.getDate() &&
          Boolean(event.id)
        )
      })
    }

    const gotoPrevMonth = () => {
      calendarRef.current?.getApi().prev()
      setCalendarDate(calendarRef.current?.getApi().getDate() || new Date())
    }
    const gotoNextMonth = () => {
      calendarRef.current?.getApi().next()
      setCalendarDate(calendarRef.current?.getApi().getDate() || new Date())
    }

    const createNewActivity = () => {
      openCreateActivityPage()
    }

    const deleteActivityHandler = async (id: string) => {
      if (deleting) return
      setDeleting(true)
      try {
        await deleteActivity(id)
        setDisplayActivities(prev => prev.filter(a => a.id !== id))
        toaster.success({
          title: 'Aktivität gelöscht',
          description: 'Die Aktivität wurde erfolgreich gelöscht.',
        })
        setActivityToDelete(null)
      } catch (e) {
        if (isInvoicedActivityConflict(e)) {
          toaster.error({
            title: 'Aktivität kann nicht gelöscht werden',
            description: getInvoicedActivityConflictMessage(e),
            type: 'error',
          })
          setActivityToDelete(null)
        } else if (e instanceof Error) {
          toaster.error({
            title: 'Fehler beim Löschen der Aktivität',
            description:
              'Bitte versuchen Sie es erneut oder kontaktieren Sie den Support. ' + e.message,
            type: 'error',
          })
        }
      } finally {
        setDeleting(false)
      }
    }

    return (
      <Flex id="activities-widget" direction="column" gap={6} align="stretch" minW={0} bg="bg">
        {/* Header with title, month navigation and create button */}
        <Flex
          justifyContent={'space-between'}
          gap={4}
          alignItems={{ base: 'start', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
        >
          <Flex
            direction="row"
            width="full"
            gap={{ base: '1', md: '4' }}
            alignItems={'center'}
            justifyContent={{ base: 'space-between', md: 'flex-start' }}
          >
            <Heading size="lg">Aktivitäten</Heading>
            <PeriodPager
              label={dayjs(calendarRef.current?.getApi().getDate()).format('MMMM YYYY')}
              previousLabel="Vorheriger Monat"
              nextLabel="Nächster Monat"
              onPrevious={gotoPrevMonth}
              onNext={gotoNextMonth}
              testIdPrefix="calendar"
            />
          </Flex>
          <Button
            id="new-activity-button"
            variant={'outline'}
            colorPalette={'grey'}
            disabled={!navDataReady}
            onClick={createNewActivity}
          >
            <LuCalendarPlus />
            <Text>Aktivität hinzufügen</Text>
          </Button>
        </Flex>
        <HStack gap={10} align="center" display={{ base: 'none', md: 'flex' }}>
          {ACTIVITY_LEGEND.map(item => (
            <Status.Root key={item.label} size="md" colorPalette={item.colorPalette}>
              <Status.Indicator />
              {item.label}
            </Status.Root>
          ))}
        </HStack>
        <Flex gap={6} direction={{ base: 'column', xl: 'row' }} align="stretch">
          <Box
            flex="1 1 auto"
            minW={0}
            display={{ base: 'none', md: 'block' }}
            overflow="hidden"
            bg="bg"
          >
            <FullCalendar
              viewClassNames="activityCalendar-container"
              plugins={[dayGridPlugin, interactionPlugin]}
              initialDate={new Date()}
              dateIncrement={{ months: 1 }}
              ref={calendarRef}
              initialView="dayGridMonth"
              events={events}
              headerToolbar={false}
              dayHeaders={false}
              dayCellContent={renderDayCell}
              eventContent={renderEvent}
              dayMaxEvents={4}
              selectable={true}
              editable={true}
              firstDay={1}
              select={onDaySelect}
              selectAllow={e => isSameDay(e.start, e.end)} // Only allow single day selection
              dayCellClassNames={'activityCalendar-cell'}
              contentHeight="auto"
              expandRows={true}
              handleWindowResize={true}
              stickyHeaderDates={false}
              eventClick={onEventClick}
              fixedWeekCount={true}
              showNonCurrentDates={true}
            />
          </Box>
          <Card.Root
            className="activityCalendar-sidebar"
            flex={{ base: '1', xl: '0 0 380px' }}
            width="full"
            h={{ base: 'auto', xl: '500px' }}
            overflowY="auto"
          >
            <Card.Body>
              <Card.Title color="blue.solid">
                {dayjs(selectedDate).format('DD. MMMM YYYY')}
              </Card.Title>
              <Flex direction={'column'} mt={4} gap={2}>
                {getEventsForSelection().length === 0 && (
                  <Text color={'fg.muted'} textStyle={'smMedium'}>
                    Keine Aktivitäten an diesem Tag.
                  </Text>
                )}
                {getEventsForSelection().map((event, index) => {
                  const isInvoiced = Boolean((event.extendedProps as any)?.invoiced)

                  const serviceCode = String(
                    (event.extendedProps as any)?.serviceCode ||
                      (event.extendedProps as any)?.serviceType ||
                      (event.extendedProps as any)?.type ||
                      event.title ||
                      ''
                  )
                    .trim()
                    .toUpperCase()
                    .replace(/[-\s]/g, '_')

                  const unitsRaw =
                    Number((event.extendedProps as any)?.units) ||
                    Number((event.extendedProps as any)?.quantity) ||
                    1

                  const hours = unitsToHours(serviceCode, unitsRaw)

                  return (
                    <Box key={index} py={3} data-testid={`activity-card-${event.id}`}>
                      <Flex align="start" justify="space-between" gap={4}>
                        <Box flex="1">
                          <Text textStyle="metadata" color="fg.muted" mb={3}>
                            {event.extendedProps?.bankLabel || 'Aktivität'}
                          </Text>

                          <Flex align="center" justify="space-between" gap={4}>
                            <Status.Root
                              size="md"
                              colorPalette={activityStatusColorPalette(serviceCode)}
                            >
                              <Status.Indicator />
                              {event.title}
                            </Status.Root>
                          </Flex>
                        </Box>
                        <VStack align="center" gap={2}>
                          <HStack gap={3} pt={1}>
                            <Button
                              variant="ghost"
                              onClick={() => editActivity(event.id as string | number)}
                              aria-label="editActivity"
                            >
                              <LuPencil size={22} />
                            </Button>
                            <Tooltip
                              disabled={!isInvoiced}
                              content={
                                <Box maxW="240px">
                                  <Text textStyle="metadata">
                                    Diese Aktivität wurde bereits in Rechnung gestellt und kann
                                    nicht gelöscht werden.
                                  </Text>
                                </Box>
                              }
                            >
                              <Button
                                variant="ghost"
                                disabled={isInvoiced}
                                onClick={() => setActivityToDelete(event.id as string)}
                                aria-label="setActivityToDelete"
                              >
                                <LuTrash2 size={22} />
                              </Button>
                            </Tooltip>
                          </HStack>
                          <Text textStyle="metadata" color="fg.muted" whiteSpace="nowrap">
                            {unitsRaw} ({hours}h)
                          </Text>
                        </VStack>
                      </Flex>
                    </Box>
                  )
                })}
              </Flex>
            </Card.Body>
          </Card.Root>
        </Flex>

        {activityToDelete && (
          <DeleteActivityModal
            loading={deleting}
            open={Boolean(activityToDelete)}
            onDelete={() => deleteActivityHandler(activityToDelete)}
            onClose={() => setActivityToDelete(null)}
          />
        )}
      </Flex>
    )
  }
)

ActivityCalendar.displayName = 'ActivityCalendar'

// export default ActivityCalendar
export default React.memo(ActivityCalendar)
